import { SWIPE_THRESHOLD_PX, SWIPE_VELOCITY_THRESHOLD } from "@/features/today/todayPagerLogic";

/** Left-edge hit zone (px). Narrow on desktop; matches iOS interactive-pop affordance. */
export const EDGE_SWIPE_ZONE_PX = 24;

/** Minimum movement before locking swipe vs scroll intent. */
export const EDGE_SWIPE_LOCK_PX = 10;

/** |dx| must exceed |dy| × ratio to count as a horizontal back swipe. */
export const EDGE_SWIPE_HORIZONTAL_RATIO = 1.5;

/** Rubber-band factor when dragging past the viewport width. */
export const EDGE_SWIPE_RUBBER_BAND_FACTOR = 0.32;

export type EdgeSwipeGestureIntent = "pending" | "back" | "scroll";

export type EdgeSwipeReleaseDecision = "commit" | "cancel";

export type EdgeSwipeBackTarget = "history" | { pathname: string; replace?: boolean };

export function isPointerInEdgeZone(clientX: number, edgeWidthPx = EDGE_SWIPE_ZONE_PX): boolean {
  return clientX >= 0 && clientX <= edgeWidthPx;
}

export function classifyEdgeSwipeIntent(dx: number, dy: number): EdgeSwipeGestureIntent {
  const absX = Math.abs(dx);
  const absY = Math.abs(dy);

  if (absX < EDGE_SWIPE_LOCK_PX && absY < EDGE_SWIPE_LOCK_PX) {
    return "pending";
  }

  if (absY > absX * EDGE_SWIPE_HORIZONTAL_RATIO) {
    return "scroll";
  }

  if (dx > 0 && absX > absY * EDGE_SWIPE_HORIZONTAL_RATIO) {
    return "back";
  }

  return "scroll";
}

/** True when release should navigate back (drag right past threshold or fast fling right). */
export function resolveEdgeSwipeBackCommit(offsetX: number, velocityX: number): boolean {
  return offsetX >= SWIPE_THRESHOLD_PX || velocityX >= SWIPE_VELOCITY_THRESHOLD;
}

export function resolveEdgeSwipeRelease(offsetX: number, velocityX: number): EdgeSwipeReleaseDecision {
  return resolveEdgeSwipeBackCommit(offsetX, velocityX) ? "commit" : "cancel";
}

/** Clamp interactive drag to the right; apply rubber-band past viewport width. */
export function clampEdgeSwipeOffset(offsetX: number, viewportWidth: number): number {
  if (offsetX <= 0 || viewportWidth <= 0) return 0;
  if (offsetX <= viewportWidth) return offsetX;
  const excess = offsetX - viewportWidth;
  return viewportWidth + excess * EDGE_SWIPE_RUBBER_BAND_FACTOR;
}

/** React Router BrowserRouter tracks stack depth on history.state.idx when available. */
export function canGoBackInHistory(): boolean {
  const state = window.history.state as { idx?: number } | null;
  if (state && typeof state.idx === "number") {
    return state.idx > 0;
  }
  return window.history.length > 1;
}

export function canCommitEdgeSwipeBack(backTarget: EdgeSwipeBackTarget): boolean {
  if (backTarget === "history") {
    return canGoBackInHistory();
  }
  return true;
}
